/**
 * Composer.MentionSuggestionList — pure presentational mention panel.
 *
 * Extracted from ``MentionAutocomplete`` (May 2026 Tiptap migration) so the
 * same panel body can be re-mounted by two drivers without duplicating
 * markup/styles:
 *
 *   1. ``MentionAutocomplete`` (textarea / legacy path) — owns its own
 *      document-level keydown listener, forwards arrows/Enter into the
 *      panel via the imperative handle.
 *   2. ``RichTextEditor`` (Tiptap path) — wires Tiptap's ``suggestion``
 *      ``onKeyDown`` callback into the same imperative handle. Tiptap
 *      keeps focus inside the ProseMirror editor view, so we cannot rely
 *      on the panel being focused; arrow keys + Enter are forwarded via
 *      ``imperativeHandle.onKeyDown``.
 *
 * DRY rationale: row markup + active-row styling + Italian copy +
 * icon mapping all live here. Drivers are thin shells that wire keyboard
 * + lifecycle hooks.
 *
 * Italian copy: "Nessun risultato", "Cerca persone, documenti, cartelle…".
 */

import * as React from "react";
import {
  File as FileIcon,
  Folder as FolderIcon,
  Mail as MailIcon,
  Plug as PlugIcon,
  User as UserIcon,
} from "lucide-react";
import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import { cn } from "../../lib/cn";
import { useI18n } from "../../lib/i18n";

/** Lucide icon hint emitted by the BE for each suggestion. */
export type MentionAutocompleteIcon =
  | "file"
  | "user"
  | "folder"
  | "plug"
  | "mail";

/** Mirrors the BE ``MentionKind`` literal — repeated here so this primitive
 * stays free of cross-package type imports. */
export type MentionAutocompleteKind =
  | "document"
  | "person"
  | "folder"
  | "connector"
  | "thread";

/** Single suggestion row. Stringly-typed for wire/audit safety. */
export interface MentionAutocompleteResult {
  readonly kind: MentionAutocompleteKind;
  readonly id: string;
  readonly display: string;
  readonly subtitle?: string | null;
  readonly icon: MentionAutocompleteIcon;
}

/**
 * Imperative API used by drivers that do NOT own DOM focus on the panel
 * (e.g. Tiptap, which keeps focus inside the ProseMirror editor view).
 *
 * Drivers forward arrow keys + Enter into ``onKeyDown`` and receive
 * ``true`` when the panel consumed the event (so they can call
 * ``event.preventDefault()`` on the upstream key event).
 */
export interface MentionSuggestionListHandle {
  /**
   * Forward a keyboard event to the panel. Returns ``true`` if the panel
   * handled it (caller should ``preventDefault``).
   */
  onKeyDown: (event: { key: string }) => boolean;
}

export interface MentionSuggestionListProps {
  /** Current ``@xxx`` query (sans the leading ``@``). Drives the empty-state copy. */
  readonly query: string;
  /** Pre-fetched, ranked suggestion list. */
  readonly results: readonly MentionAutocompleteResult[];
  /** Loading flag — surfaces a subtle "Cerco…" hint without flicker. */
  readonly loading?: boolean;
  /** Called when the user picks a row (click or Enter forwarded by driver). */
  readonly onSelect: (mention: MentionAutocompleteResult) => void;
  /** Optional extra class for the wrapping panel. */
  readonly className?: string;
}

const ICON_BY_HINT: Record<
  MentionAutocompleteIcon,
  React.ComponentType<{ size?: number; className?: string }>
> = {
  file: FileIcon,
  user: UserIcon,
  folder: FolderIcon,
  plug: PlugIcon,
  mail: MailIcon,
};

/** Localised kind chip label — resolved at render time via Lingui. */
const KIND_LABEL: Record<MentionAutocompleteKind, MessageDescriptor> = {
  document: msg`Documento`,
  person: msg`Persona`,
  folder: msg`Cartella`,
  connector: msg`Connettore`,
  thread: msg`Conversazione`,
};

export const MentionSuggestionList = React.forwardRef<
  MentionSuggestionListHandle,
  MentionSuggestionListProps
>(({ query, results, loading, onSelect, className }, ref) => {
  const { i18n } = useI18n();
  const [activeIndex, setActiveIndex] = React.useState(0);

  // Stable signature so the reset only fires when the underlying IDs change,
  // not on every parent re-render that hands us a freshly-mapped array.
  const idSignature = React.useMemo(
    () => results.map((r) => `${r.kind}:${r.id}`).join("|"),
    [results],
  );
  React.useEffect(() => {
    void idSignature;
    setActiveIndex(0);
  }, [idSignature]);

  // Imperative keyboard API for drivers that keep focus elsewhere
  // (Tiptap ProseMirror editor view).
  React.useImperativeHandle(
    ref,
    () => ({
      onKeyDown: (event) => {
        if (results.length === 0) return false;
        if (event.key === "ArrowDown") {
          setActiveIndex((i) => Math.min(i + 1, results.length - 1));
          return true;
        }
        if (event.key === "ArrowUp") {
          setActiveIndex((i) => Math.max(i - 1, 0));
          return true;
        }
        if (event.key === "Enter") {
          const m = results[activeIndex];
          if (m) {
            onSelect(m);
            return true;
          }
        }
        return false;
      },
    }),
    [activeIndex, results, onSelect],
  );

  return (
    <div
      aria-label={i18n._(msg`Suggerimenti menzione`)}
      className={cn(
        "rounded-lg border border-hairline bg-white/95 shadow-[0_4px_16px_-6px_rgba(0,0,0,0.10)]",
        "p-1 backdrop-blur-md",
        "animate-chip-in",
        // Cap the dropdown height — long match lists shouldn't push the
        // composer off-screen. Internal scroll keeps the keyboard nav UX.
        "max-h-72 overflow-y-auto",
        className,
      )}
    >
      {results.length === 0 ? (
        <div className="px-2 py-2 text-[11px] text-ink-3">
          {loading
            ? i18n._(msg`Cerco persone, documenti, cartelle…`)
            : query.length === 0
              ? i18n._(msg`Cerca persone, documenti, cartelle…`)
              : i18n._(msg`Nessun risultato`)}
        </div>
      ) : (
        <div role="menu">
          {results.map((m, i) => {
            const Icon = ICON_BY_HINT[m.icon] ?? FileIcon;
            const active = i === activeIndex;
            const optionId = `mention-opt-${m.kind}-${m.id}`;
            return (
              <button
                key={optionId}
                id={optionId}
                type="button"
                role="menuitem"
                aria-current={active ? "true" : undefined}
                onMouseEnter={() => setActiveIndex(i)}
                onMouseDown={(e) => {
                  // Prevent editor blur — selection runs synchronously
                  // and the driver re-focuses the editor afterward.
                  e.preventDefault();
                }}
                onClick={() => onSelect(m)}
                className={cn(
                  "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left",
                  "transition-colors duration-150 ease-out",
                  active
                    ? "bg-ando-orange/10 text-ink-1"
                    : "text-ink-2 hover:bg-ink-1/[0.04]",
                )}
              >
                <Icon
                  size={13}
                  className={cn(
                    "shrink-0",
                    active ? "text-ando-orange" : "text-ink-3",
                  )}
                />
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate font-medium text-[11.5px] text-ink-1">
                    {m.display}
                  </span>
                  {m.subtitle ? (
                    <span className="truncate text-[10.5px] text-ink-3">
                      {m.subtitle}
                    </span>
                  ) : null}
                </span>
                <span
                  className={cn(
                    "shrink-0 rounded-full border px-1.5 py-0.5 font-medium text-[9.5px] uppercase tracking-wide",
                    active
                      ? "border-ando-orange/40 bg-ando-orange/10 text-ando-orange"
                      : "border-hairline bg-ink-1/[0.03] text-ink-3",
                  )}
                >
                  {i18n._(KIND_LABEL[m.kind])}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
});
MentionSuggestionList.displayName = "Composer.MentionSuggestionList";
